import { useCallback, useEffect, useState } from "react";
import { listRegisteredAccounts, patchAccount } from "../api/accounts";
import type { AccountPatch, PostedRow, RegisteredAccount } from "../types/account";
import { refusalSentence } from "./useServerAnswer";

/** What a PATCH answered that the list does not serve (the list sends both empty). */
interface Answered {
  notices: string[];
  posted_rows: PostedRow[];
}

export interface RegisteredAccountsState {
  accounts: RegisteredAccount[];
  loading: boolean;
  /** The server's sentence when the list could not be read, else "". */
  error: string;
  /** Answer one row's question; rejects with the server's refusal for the form to show. */
  answer: (rowId: number, body: AccountPatch) => Promise<void>;
}

function withAnswered(row: RegisteredAccount, answered: Answered | undefined): RegisteredAccount {
  if (!answered) return row;
  return { ...row, notices: answered.notices, posted_rows: answered.posted_rows };
}

/**
 * The client's registered accounts, loaded again whenever `refreshKey` moves.
 *
 * A PATCH's answer replaces its row at once. Its `notices` and `posted_rows`
 * are kept for that row across later loads, because the list never serves
 * them: the personal move's list stays on screen after the review list
 * refreshes. `onChanged` runs after every answer the server accepted.
 */
export function useRegisteredAccounts(
  clientId: number,
  refreshKey: number,
  onChanged: () => void,
): RegisteredAccountsState {
  const [rows, setRows] = useState<RegisteredAccount[]>([]);
  const [answered, setAnswered] = useState<Record<number, Answered>>({});
  // The read the screen last finished, and its error ("" when it succeeded):
  // a later successful read clears an earlier error, and the section is
  // loading while the finished read is not the current one.
  const [read, setRead] = useState<{ key: string; error: string } | null>(null);
  const key = `${clientId}:${refreshKey}`;

  useEffect(() => {
    let cancelled = false;
    listRegisteredAccounts(clientId)
      .then((data) => {
        if (cancelled) return;
        setRows(data);
        setRead({ key, error: "" });
      })
      .catch((err: unknown) => {
        if (!cancelled) setRead({ key, error: refusalSentence(err) });
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, key]);

  const answer = useCallback(
    async (rowId: number, body: AccountPatch) => {
      const row = await patchAccount(clientId, rowId, body);
      setRows((prev) => prev.map((r) => (r.id === rowId ? row : r)));
      if (row.notices.length > 0 || row.posted_rows.length > 0) {
        setAnswered((prev) => ({
          ...prev,
          [rowId]: { notices: row.notices, posted_rows: row.posted_rows },
        }));
      }
      onChanged();
    },
    [clientId, onChanged],
  );

  const accounts = rows.map((r) => withAnswered(r, answered[r.id]));
  const loading = read?.key !== key;
  return { accounts, loading, error: loading ? "" : (read?.error ?? ""), answer };
}
